import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface FriendUser {
  friendshipId: string;
  userId: string;
  displayName: string;
}

interface SearchUser {
  userId: string;
  displayName: string;
}

const REQUEST_ERRORS: Record<string, string> = {
  user_not_found: 'friends.userNotFound',
  cannot_friend_self: 'friends.cannotFriendSelf',
  already_friends: 'friends.alreadyFriends',
  request_pending: 'friends.requestPending',
};

export function FriendsPage() {
  const { user, guest, loading: authLoading } = useAuth();
  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [pendingIncoming, setPendingIncoming] = useState<FriendUser[]>([]);
  const [pendingOutgoing, setPendingOutgoing] = useState<FriendUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const loadFriends = useCallback(async () => {
    const data = await api<{
      friends: FriendUser[];
      pendingIncoming: FriendUser[];
      pendingOutgoing: FriendUser[];
    }>('/friends');
    setFriends(data.friends);
    setPendingIncoming(data.pendingIncoming);
    setPendingOutgoing(data.pendingOutgoing);
  }, []);

  useEffect(() => {
    if (authLoading || !user || guest) {
      setLoading(false);
      return;
    }
    loadFriends()
      .catch(() => setError(t('friends.error')))
      .finally(() => setLoading(false));
  }, [authLoading, user, guest, loadFriends]);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setSearchBusy(true);
    setActionMsg('');
    try {
      const data = await api<{ results: SearchUser[] }>(`/friends/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data.results);
    } catch {
      setActionMsg(t('friends.searchError'));
    } finally {
      setSearchBusy(false);
    }
  };

  const sendRequest = async (opts: { userId?: string; displayName?: string }) => {
    setInviteBusy(true);
    setActionMsg('');
    try {
      await api('/friends/request', {
        method: 'POST',
        body: JSON.stringify(opts),
      });
      setActionMsg(t('friends.requestSent'));
      setInviteName('');
      setSearchResults([]);
      await loadFriends();
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } }).data?.error;
      const key = code && REQUEST_ERRORS[code];
      setActionMsg(key ? t(key) : t('friends.requestError'));
    } finally {
      setInviteBusy(false);
    }
  };

  const acceptRequest = async (friendshipId: string) => {
    await api(`/friends/${friendshipId}/accept`, { method: 'POST' });
    await loadFriends();
  };

  const rejectRequest = async (friendshipId: string) => {
    await api(`/friends/${friendshipId}/reject`, { method: 'POST' });
    await loadFriends();
  };

  const removeFriend = async (friendshipId: string) => {
    await api(`/friends/${friendshipId}`, { method: 'DELETE' });
    await loadFriends();
  };

  if (authLoading) {
    return <main className="page friends"><p className="loading">{t('common.loading')}</p></main>;
  }

  if (!user || guest) {
    return (
      <main className="page friends">
        <h1>{t('friends.title')}</h1>
        <p className="muted">{t('friends.loginPrompt')}</p>
        <Link to="/login" className="btn primary">{t('landing.login')}</Link>
      </main>
    );
  }

  return (
    <main className="page friends">
      <h1>{t('friends.title')}</h1>
      <p className="sub">{t('friends.sub')}</p>

      {error && <p className="error" role="alert">{error}</p>}
      {actionMsg && <p className="muted">{actionMsg}</p>}

      <section className="friends-section">
        <h2>{t('friends.inviteTitle')}</h2>
        <form
          className="friends-search"
          onSubmit={(e) => {
            e.preventDefault();
            const name = inviteName.trim();
            if (name) sendRequest({ displayName: name });
          }}
        >
          <input
            type="text"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            placeholder={t('friends.invitePlaceholder')}
            maxLength={32}
            disabled={inviteBusy}
          />
          <button type="submit" className="btn primary" disabled={inviteBusy || !inviteName.trim()}>
            {inviteBusy ? t('common.loading') : t('friends.invite')}
          </button>
        </form>
      </section>

      <section className="friends-section">
        <h2>{t('friends.searchTitle')}</h2>
        <form className="friends-search" onSubmit={handleSearch}>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('friends.searchPlaceholder')}
            minLength={2}
            disabled={searchBusy}
          />
          <button type="submit" className="btn secondary" disabled={searchBusy || searchQuery.trim().length < 2}>
            {searchBusy ? t('common.loading') : t('friends.search')}
          </button>
        </form>
        {searchResults.length > 0 && (
          <ul className="friends-results">
            {searchResults.map((u) => (
              <li key={u.userId}>
                <span>{u.displayName}</span>
                <button
                  type="button"
                  className="btn ghost admin-btn-sm"
                  disabled={inviteBusy}
                  onClick={() => sendRequest({ userId: u.userId })}
                >
                  {t('friends.add')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {loading ? (
        <p className="loading">{t('common.loading')}</p>
      ) : (
        <>
          {pendingIncoming.length > 0 && (
            <section className="friends-section">
              <h2>{t('friends.incoming')}</h2>
              <ul className="friends-list">
                {pendingIncoming.map((f) => (
                  <li key={f.friendshipId}>
                    <span>{f.displayName}</span>
                    <div className="friends-actions">
                      <button type="button" className="btn primary admin-btn-sm" onClick={() => acceptRequest(f.friendshipId)}>
                        {t('friends.accept')}
                      </button>
                      <button type="button" className="btn ghost admin-btn-sm" onClick={() => rejectRequest(f.friendshipId)}>
                        {t('friends.reject')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {pendingOutgoing.length > 0 && (
            <section className="friends-section">
              <h2>{t('friends.outgoing')}</h2>
              <ul className="friends-list">
                {pendingOutgoing.map((f) => (
                  <li key={f.friendshipId}>
                    <span>{f.displayName}</span>
                    <span className="muted">{t('friends.pending')}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="friends-section">
            <h2>{t('friends.list')}</h2>
            {friends.length === 0 ? (
              <p className="muted">{t('friends.empty')}</p>
            ) : (
              <ul className="friends-list">
                {friends.map((f) => (
                  <li key={f.friendshipId}>
                    <span>{f.displayName}</span>
                    <div className="friends-actions">
                      <button type="button" className="btn ghost admin-btn-sm" onClick={() => removeFriend(f.friendshipId)}>
                        {t('friends.remove')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <nav className="result-nav">
        <Link to="/leaderboard/amis">{t('leaderboard.scopeFriends')}</Link>
        <Link to="/archives">{t('archives.title')}</Link>
        <Link to="/profile">{t('profile.title')}</Link>
      </nav>
    </main>
  );
}
